import { Copy, LayoutTemplate, Pencil, Plus, Trash2 } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { MissionTemplate } from '@orgus/protocol';
import { Badge, Button, ConfirmDialog, ErrorState, IconButton, Panel, SkeletonRows, Tooltip, plural, toast } from '@orgus/ui';
import { deleteTemplate, loadTemplates, useOnboarding, useOnboardingData } from '../../onboarding/client.ts';
import { describeTemplate, orderTemplates } from '../../onboarding/model.ts';
import { TemplateDialog } from '../../onboarding/TemplateDialog.tsx';
import { TemplateIcon } from '../../onboarding/TemplateIcon.tsx';
import type { SettingsSection } from '../registry.ts';

function MissionTemplates() {
  useOnboardingData({ templates: true });
  const phase = useOnboarding((s) => s.templatesPhase);
  const error = useOnboarding((s) => s.templatesError);
  const raw = useOnboarding((s) => s.templates);
  const role = useOnboarding((s) => s.probes.auth?.me?.role ?? 'owner');
  const templates = useMemo(() => orderTemplates(raw), [raw]);
  const canEdit = role === 'owner' || role === 'admin';
  const [dialog, setDialog] = useState<{ base: MissionTemplate | null; copy: boolean } | null>(null);
  const [removing, setRemoving] = useState<MissionTemplate | null>(null);
  const own = templates.filter((t) => !t.builtIn).length;

  return (
    <div className="flex max-w-240 flex-col gap-4">
      <Panel
        title="Templates"
        sub={own ? `4 built in, ${own} of this hub's own` : '4 built in'}
        icon={<LayoutTemplate />}
        flush
        actions={
          canEdit ? (
            <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setDialog({ base: null, copy: false })}>
              Add template
            </Button>
          ) : (
            <span className="px-2 text-caption text-fg-subtle">Admins add templates</span>
          )
        }
      >
        {phase === 'loading' && !templates.length ? (
          <SkeletonRows rows={4} />
        ) : phase === 'error' && !templates.length ? (
          <ErrorState error={error} onRetry={() => loadTemplates()} />
        ) : (
          <ul aria-label="Mission templates">
            {templates.map((t) => (
              <li key={t.id} className="group flex min-h-row-2line items-center gap-3 border-b border-border-subtle px-4 py-2 last:border-b-0">
                <TemplateIcon icon={t.icon} className="size-4 shrink-0 text-fg-subtle" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-ui font-medium text-fg">{t.title}</span>
                    {t.builtIn ? <Badge>Built in</Badge> : <Badge tone="accent">This hub</Badge>}
                  </span>
                  <span className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
                    <span className="truncate">{describeTemplate(t)}</span>
                    {t.fields.length ? <span className="shrink-0 num">{plural(t.fields.length, 'field')}</span> : null}
                  </span>
                </span>
                {canEdit && (
                  <span className="flex shrink-0 items-center gap-1">
                    <IconButton label={`Duplicate ${t.title}`} icon={<Copy />} onClick={() => setDialog({ base: t, copy: true })} />
                    {t.builtIn ? (
                      <Tooltip content="Built-in templates are the same on every hub. Duplicate one to change it.">
                        <span className="inline-flex">
                          <IconButton label={`Edit ${t.title} (built in, duplicate it instead)`} icon={<Pencil />} disabled />
                        </span>
                      </Tooltip>
                    ) : (
                      <>
                        <IconButton label={`Edit ${t.title}`} icon={<Pencil />} onClick={() => setDialog({ base: t, copy: false })} />
                        <IconButton label={`Delete ${t.title}`} icon={<Trash2 />} onClick={() => setRemoving(t)} />
                      </>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <TemplateDialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)} base={dialog?.base ?? null} copy={dialog?.copy ?? false} />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={`Delete template "${removing?.title ?? ''}"?`}
        description="It is no longer offered when anyone on this hub starts a mission. Missions already started from it keep running and keep their history."
        confirmLabel="Delete template"
        onConfirm={async () => {
          if (!removing) return;
          await deleteTemplate(removing.id);
          toast.success('Template deleted', removing.title);
        }}
      />
    </div>
  );
}

const section: SettingsSection = {
  id: 'mission-templates',
  title: 'Mission templates',
  description: 'Ready-made missions people start in one click.',
  order: 40,
  group: 'agents',
  keywords: ['templates', 'missions', 'presets'],
  icon: LayoutTemplate,
  Component: MissionTemplates,
};
export default section;
